import React, { useEffect, useRef, useState } from 'react';
import { whereItBelongs, halalData } from '../data/mock';
import { Truck, Store, Hotel, PartyPopper, CheckCircle2 } from 'lucide-react';

const iconMap = {
  Truck: Truck,
  Store: Store,
  Hotel: Hotel,
  PartyPopper: PartyPopper,
};

const WhereItBelongs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-1/4 left-0 w-72 h-72 bg-[#c9a962]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#009246]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Where It Belongs Section */}
        <div className="mb-24">
          <div
            className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="text-[#c9a962] text-sm font-semibold tracking-[0.2em] uppercase">
              Availability
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[#1e1919] mt-4 mb-6">
              Where It Belongs
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whereItBelongs.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div
                  key={item.id}
                  className={`group text-center p-8 bg-white rounded-2xl shadow-sm border border-[#e8e3db] transition-all duration-500 hover:shadow-xl hover:border-[#c9a962]/30 hover:-translate-y-2 ${
                    isVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 bg-[#c9a962]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#c9a962]/20 transition-colors">
                    {IconComponent && (
                      <IconComponent className="w-8 h-8 text-[#c9a962]" />
                    )}
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#1e1919] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#736c64]">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Halal Excellence Section */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#009246]/10 rounded-full mb-6">
              <span className="text-2xl">🇦🇪</span>
              <span className="text-[#009246] font-semibold text-sm">UAE Certified</span>
            </div>
            
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[#1e1919] mb-4">
              {halalData.title}
            </h2>
            <p className="text-xl text-[#61525a] mb-6">
              {halalData.subtitle}
            </p>
            <p className="text-lg text-[#736c64] leading-relaxed mb-8">
              {halalData.description}
            </p>

            <div className="space-y-4">
              {halalData.certifications.map((cert, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 className="w-6 h-6 text-[#009246]" />
                  <span className="text-[#1e1919] font-medium">{cert}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Image */}
          <div
            className={`relative transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#009246]/10 via-transparent to-[#ce2b37]/10 rounded-3xl blur-xl" />
              <img
                src="/images/image9.jpg"
                alt="Halal certified CRISPYzza"
                className="relative rounded-2xl w-full h-[400px] object-cover shadow-2xl"
              />
              
              {/* Badge */}
              <div className="absolute -bottom-6 -right-6 bg-[#1e1919] text-white rounded-2xl p-6 shadow-xl">
                <p className="text-sm text-[#c9a962] font-medium uppercase tracking-wider mb-1">
                  100%
                </p>
                <p className="font-display text-2xl font-bold">
                  Halal Certified
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhereItBelongs;
